import {
  effectivePermissions,
  type InviteMemberInput,
  type InviteMemberResult,
  inviteMemberInput,
  type Member,
  type MemberPasswordResetResult,
  type Role,
  type RoleKey,
} from '@facture/contracts'
import { zodResolver } from '@hookform/resolvers/zod'
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import {
  CheckIcon,
  CopyIcon,
  KeyRoundIcon,
  MailIcon,
  MoreHorizontalIcon,
  PowerIcon,
  ShieldCheckIcon,
  SlidersHorizontalIcon,
  Trash2Icon,
  UserPlusIcon,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { Badge, type BadgeVariant } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { confirm } from '@/components/ui/confirm'
import { columnHelper, DataTable } from '@/components/ui/data-table'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Avatar, Tooltip } from '@/components/ui/misc'
import { Select } from '@/components/ui/select'
import { MemberPermissionsSheet } from '@/features/members/member-permissions-sheet'
import { api } from '@/lib/api'
import { copyText } from '@/lib/clipboard'
import { formatRelative } from '@/lib/format'
import { useActiveMembership, useCan, useSession } from '@/lib/session'

export const Route = createFileRoute('/_app/settings/team')({
  staticData: { crumb: 'Equipo' },
  component: TeamSettings,
})

const membersQuery = queryOptions({ queryKey: ['members'], queryFn: () => api.get<Member[]>('/members') })
const rolesQuery = queryOptions({
  queryKey: ['roles'],
  queryFn: () => api.get<Role[]>('/roles'),
  staleTime: 300_000,
})

const ROLE_VARIANT: Record<RoleKey, BadgeVariant> = {
  owner: 'primary',
  admin: 'info',
  accountant: 'success',
  seller: 'warning',
  viewer: 'neutral',
}

const col = columnHelper<Member>()

function TeamSettings() {
  const members = useQuery(membersQuery)
  const roles = useQuery(rolesQuery)
  const canManage = useCan('users:manage')
  const me = useSession((s) => s.user?.id)
  const isSuperadmin = useSession((s) => s.user?.isSuperadmin ?? false)
  const membership = useActiveMembership()
  const myRole = membership?.role
  // Solo un propietario (o la plataforma) otorga, quita o modifica el rol de propietario.
  const canManageOwners = isSuperadmin || myRole === 'owner'
  const queryClient = useQueryClient()
  const [inviteOpen, setInviteOpen] = useState(false)
  const [credentials, setCredentials] = useState<{ email: string; password: string } | null>(null)
  const [permissionsOf, setPermissionsOf] = useState<string | null>(null)

  const update = useMutation({
    mutationFn: ({ id, ...body }: { id: string; role?: RoleKey; status?: 'active' | 'disabled' }) =>
      api.patch<Member>(`/members/${id}`, body),
    onSuccess: () => {
      toast.success('Miembro actualizado')
      queryClient.invalidateQueries({ queryKey: ['members'] })
    },
  })

  const columns = useMemo(() => {
    const roleName = (key: RoleKey) => roles.data?.find((r) => r.key === key)?.name ?? key
    return [
      col.accessor('name', {
        header: 'Miembro',
        cell: ({ row }) => (
          <div className="flex items-center gap-3">
            <Avatar name={row.original.name} />
            <div className="min-w-0">
              <div className="flex items-center gap-2 font-medium">
                {row.original.name}
                {row.original.userId === me && (
                  <span className="text-muted-foreground text-xs font-normal">(vos)</span>
                )}
              </div>
              <div className="truncate text-muted-foreground text-xs">{row.original.email}</div>
            </div>
          </div>
        ),
      }),
      col.accessor('role', {
        header: 'Rol',
        cell: ({ row }) => (
          <div className="flex flex-wrap items-center gap-1.5">
            <Badge variant={ROLE_VARIANT[row.original.role]}>{roleName(row.original.role)}</Badge>
            {row.original.customPermissions && (
              <Tooltip content={`${row.original.effectivePermissions.length} acciones habilitadas`}>
                <Badge variant="warning" className="gap-1">
                  <SlidersHorizontalIcon /> Permisos ajustados
                </Badge>
              </Tooltip>
            )}
          </div>
        ),
      }),
      col.accessor('status', {
        header: 'Estado',
        meta: { hideOnMobile: true },
        cell: ({ getValue }) =>
          getValue() === 'active' ? (
            <Badge variant="success" dot>
              Activo
            </Badge>
          ) : (
            <Badge dot>Deshabilitado</Badge>
          ),
      }),
      col.accessor('lastLoginAt', {
        header: 'Último ingreso',
        meta: { hideOnMobile: true },
        cell: ({ getValue }) => (
          <span className="text-muted-foreground text-sm">
            {getValue() ? formatRelative(getValue()) : 'Nunca'}
          </span>
        ),
      }),
      col.display({
        id: 'actions',
        header: '',
        meta: { align: 'right', className: 'w-12' },
        cell: ({ row }) =>
          canManage && row.original.userId !== me && (canManageOwners || row.original.role !== 'owner') ? (
            <MemberActions
              member={row.original}
              roles={(roles.data ?? []).filter((r) => canManageOwners || r.key !== 'owner')}
              onRole={(role) => update.mutate({ id: row.original.id, role })}
              onPermissions={
                row.original.role === 'owner' ? undefined : () => setPermissionsOf(row.original.id)
              }
              onToggle={() =>
                update.mutate({
                  id: row.original.id,
                  status: row.original.status === 'active' ? 'disabled' : 'active',
                })
              }
            />
          ) : null,
      }),
    ]
  }, [canManage, canManageOwners, me, roles.data, update.mutate])
  const plan = membership?.plan ?? 'full'
  const modules = membership?.modules ?? []

  return (
    <div className="grid gap-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-semibold text-lg tracking-tight">Equipo</h2>
          <p className="text-muted-foreground text-sm">Invitá personas y definí qué puede hacer cada una.</p>
        </div>
        {canManage && (
          <Button onClick={() => setInviteOpen(true)}>
            <UserPlusIcon /> Invitar miembro
          </Button>
        )}
      </div>

      <DataTable
        columns={columns}
        data={members.data}
        loading={members.isLoading}
        getRowId={(m) => m.id}
        isRowMuted={(m) => m.status !== 'active'}
      />

      <Card>
        <CardHeader>
          <div>
            <CardTitle>Roles del sistema</CardTitle>
            <p className="mt-1 text-muted-foreground text-sm">Permisos incluidos en cada rol.</p>
          </div>
          <ShieldCheckIcon className="size-5 text-muted-foreground" />
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {roles.data?.map((role) => (
            <div key={role.id} className="rounded-lg border border-border p-4">
              <div className="flex items-center justify-between gap-2">
                <Badge variant={ROLE_VARIANT[role.key]}>{role.name}</Badge>
                <span className="text-muted-foreground text-xs tabular">
                  {effectivePermissions({ plan, modules, role: role.permissions }).length} permisos
                </span>
              </div>
              {role.description && <p className="mt-2 text-muted-foreground text-sm">{role.description}</p>}
            </div>
          ))}
        </CardContent>
      </Card>

      <InviteDialog
        open={inviteOpen}
        roles={(roles.data ?? []).filter((r) => canManageOwners || r.key !== 'owner')}
        onClose={() => setInviteOpen(false)}
        onInvited={(result) => {
          if (result.temporaryPassword) {
            setCredentials({ email: result.member.email, password: result.temporaryPassword })
          }
        }}
      />
      <CredentialsDialog credentials={credentials} onClose={() => setCredentials(null)} />
      <MemberPermissionsSheet
        memberId={permissionsOf}
        company={{ plan, modules }}
        onClose={() => setPermissionsOf(null)}
      />
    </div>
  )
}

function MemberActions({
  member,
  roles,
  onRole,
  onPermissions,
  onToggle,
}: {
  member: Member
  roles: Role[]
  onRole: (role: RoleKey) => void
  /** Sin él (propietario) no se ofrece: siempre tiene todos los permisos. */
  onPermissions?: () => void
  onToggle: () => void
}) {
  const queryClient = useQueryClient()
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon-sm" aria-label="Acciones">
          <MoreHorizontalIcon />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <DropdownMenuLabel>{member.name}</DropdownMenuLabel>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <KeyRoundIcon /> Cambiar rol
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuRadioGroup value={member.role} onValueChange={(v) => onRole(v as RoleKey)}>
              {roles.map((r) => (
                <DropdownMenuRadioItem key={r.key} value={r.key}>
                  {r.name}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        {onPermissions && (
          <DropdownMenuItem onSelect={onPermissions}>
            <SlidersHorizontalIcon /> Permisos
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onSelect={onToggle}>
          <PowerIcon /> {member.status === 'active' ? 'Deshabilitar' : 'Habilitar'}
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() =>
            confirm({
              title: `¿Enviar enlace de contraseña a ${member.name}?`,
              description: `Le llegará un correo a ${member.email} para elegir una contraseña nueva. El enlace vale 1 hora y, al usarlo, se cierran sus sesiones abiertas.`,
              confirmLabel: 'Enviar enlace',
              onConfirm: async () => {
                const result = await api.post<MemberPasswordResetResult>(
                  `/members/${member.id}/reset-password`,
                )
                if (result.simulated)
                  toast.warning(
                    'El sistema no tiene correo configurado: el enlace no salió. Avisá a soporte.',
                  )
                else toast.success(`Enviamos el enlace a ${member.email}`)
              },
            })
          }
        >
          <MailIcon /> Restablecer contraseña
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          destructive
          onSelect={() =>
            confirm({
              title: `¿Quitar a ${member.name}?`,
              description: 'Perderá el acceso a esta compañía. Su usuario seguirá existiendo.',
              confirmLabel: 'Quitar del equipo',
              destructive: true,
              onConfirm: async () => {
                await api.delete(`/members/${member.id}`)
                toast.success('Miembro quitado')
                await queryClient.invalidateQueries({ queryKey: ['members'] })
              },
            })
          }
        >
          <Trash2Icon /> Quitar del equipo
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function InviteDialog({
  open,
  roles,
  onClose,
  onInvited,
}: {
  open: boolean
  roles: Role[]
  onClose: () => void
  onInvited: (result: InviteMemberResult) => void
}) {
  const queryClient = useQueryClient()
  const form = useForm<InviteMemberInput>({
    resolver: zodResolver(inviteMemberInput),
    defaultValues: { email: '', name: '', role: 'seller' },
  })
  const invite = useMutation({
    mutationFn: (values: InviteMemberInput) => api.post<InviteMemberResult>('/members', values),
    onSuccess: (result) => {
      toast.success(`${result.member.name} se unió al equipo`)
      queryClient.invalidateQueries({ queryKey: ['members'] })
      form.reset()
      onClose()
      onInvited(result)
    },
  })
  const { errors } = form.formState

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="sm">
        <form onSubmit={form.handleSubmit((v) => invite.mutate(v))} noValidate>
          <DialogHeader>
            <DialogTitle>Invitar miembro</DialogTitle>
            <DialogDescription>
              Si la persona no tiene cuenta, la creamos con una contraseña temporal.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="grid gap-4">
            <Field label="Nombre completo" error={errors.name} required>
              {(c) => <Input {...c} {...form.register('name')} autoFocus />}
            </Field>
            <Field label="Correo electrónico" error={errors.email} required>
              {(c) => <Input {...c} {...form.register('email')} type="email" />}
            </Field>
            <Controller
              control={form.control}
              name="role"
              render={({ field, fieldState }) => (
                <Field label="Rol" error={fieldState.error} required>
                  {(c) => (
                    <Select
                      {...c}
                      value={field.value}
                      onValueChange={field.onChange}
                      options={roles.map((r) => ({
                        value: r.key,
                        label: r.name,
                        description: r.description ?? undefined,
                      }))}
                    />
                  )}
                </Field>
              )}
            />
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" loading={invite.isPending}>
              Invitar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function CredentialsDialog({
  credentials,
  onClose,
}: {
  credentials: { email: string; password: string } | null
  onClose: () => void
}) {
  const [copied, setCopied] = useState(false)
  return (
    <Dialog open={!!credentials} onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Contraseña temporal</DialogTitle>
          <DialogDescription>
            Compartila por un canal seguro. No se volverá a mostrar y la persona tendrá que cambiarla al
            ingresar por primera vez.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="grid gap-3">
          <div className="rounded-lg border border-border bg-subtle p-4">
            <div className="text-muted-foreground text-xs">Usuario</div>
            <div className="font-medium text-sm">{credentials?.email}</div>
            <div className="mt-3 text-muted-foreground text-xs">Contraseña</div>
            <div className="font-mono text-base tracking-wide">{credentials?.password}</div>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={async () => {
              if (!credentials) return
              const ok = await copyText(`${credentials.email}\n${credentials.password}`, {
                label: 'Credenciales de acceso',
                successMessage: false,
              })
              if (!ok) return
              setCopied(true)
              setTimeout(() => setCopied(false), 1500)
            }}
          >
            {copied ? <CheckIcon /> : <CopyIcon />} {copied ? 'Copiado' : 'Copiar'}
          </Button>
          <Button onClick={onClose}>Listo</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
